// utils/dataUtils.js
import ExcelJS from 'exceljs'; // Replaced xlsx with exceljs
import { saveAs } from 'file-saver';
import Papa from 'papaparse';

/*
 * Formats a timestamp into a human-readable string.
 * @param {Object|string|Date} value - The timestamp value to format.
 * @returns {string} - Formatted date string like "5 June 2025 at 19:54:19 CEST".
 */
const formatTimestamp = (value) => {
  let date;

  if (value && typeof value === 'object' && 'seconds' in value && 'nanoseconds' in value) {
    date = new Date(value.seconds * 1000 + value.nanoseconds / 1000000);
  } else if (typeof value === 'string' && value.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/)) {
    date = new Date(value);
  } else if (value instanceof Date) {
    date = value;
  } else if (typeof value === 'string' && value.match(/^\d{1,2}\s\w+\s\d{4}\sat\s\d{2}:\d{2}:\d{2}\s\w+$/)) {
    // Handle already formatted string like "5 June 2025 at 19:54:19 CEST"
    const parts = value.match(/^(\d{1,2})\s(\w+)\s(\d{4})\sat\s(\d{2}:\d{2}:\d{2})\s(\w+)$/);
    if (parts) {
      const [, day, month, year, time] = parts;
      const monthNames = [
        'January', 'February', 'March', 'April', 'May', 'June',
        'July', 'August', 'September', 'October', 'November', 'December'
      ];
      const monthIndex = monthNames.indexOf(month);
      if (monthIndex === -1) {
        console.warn('Invalid month name:', month);
        return String(value);
      }
      const dateString = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${time}`;
      date = new Date(dateString);
    } else {
      console.warn('Invalid formatted date string:', value);
      return String(value);
    }
  } else {
    return String(value);
  }

  if (isNaN(date.getTime())) {
    console.warn('Invalid date from Timestamp:', value);
    return String(value);
  }

  const formatted = date.toLocaleString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: 'Europe/Paris',
    timeZoneName: 'short',
  }).replace(',', ' at');
  return formatted;
};

/*
 * Recursively formats timestamps in a data object or array.
 * @param {Object|Array} data - The data to process.
 * @param {string} path - The current path for logging (optional).
 * @returns {Object|Array} - The data with timestamps formatted.
 */
const formatTimestampsInData = (data, path = '') => {

  if (data && typeof data === 'object' && 'seconds' in data && 'nanoseconds' in data) {
    return formatTimestamp(data);
  } else if (Array.isArray(data)) {
    return data.map((item, index) => formatTimestampsInData(item, `${path}[${index}]`));
  } else if (data && typeof data === 'object') {
    const newObj = {};
    Object.keys(data).forEach(key => {
      newObj[key] = formatTimestampsInData(data[key], path ? `${path}.${key}` : key);
    });
    return newObj;
  }
  return data;
};

/*
 * Expands nested arrays and objects into a flat structure for CSV export.
 * @param {Array} data - The array of objects to flatten.
 * @returns {Object} - An object with expandedHeaders and expandedData.
 */
const expandArrayColumns = (data) => {
  if (!data || data.length === 0) return { expandedHeaders: [], expandedData: [] };

  const formattedData = data.map(row => formatTimestampsInData(row));
  const arrayLengths = {};
  const mapKeys = {};
  const nestedArrayLengths = {};

  formattedData.forEach(row => {
    Object.keys(row).forEach(key => {
      const value = row[key];
      if (Array.isArray(value)) {
        arrayLengths[key] = Math.max(arrayLengths[key] || 0, value.length);
        value.forEach((item, idx) => {
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            Object.keys(item).forEach(subKey => {
              if (Array.isArray(item[subKey])) {
                if (!nestedArrayLengths[`${key}.${idx}`]) {
                  nestedArrayLengths[`${key}.${idx}`] = {};
                }
                nestedArrayLengths[`${key}.${idx}`][subKey] = Math.max(
                  nestedArrayLengths[`${key}.${idx}`][subKey] || 0,
                  item[subKey].length
                );
              }
            });
          }
        });
      } else if (value && typeof value === 'object' && !Array.isArray(value)) {
        if (!mapKeys[key]) {
          mapKeys[key] = new Set();
        }
        Object.keys(value).forEach(mapKey => {
          mapKeys[key].add(mapKey);
          const subObject = value[mapKey];
          if (subObject && typeof subObject === 'object' && !Array.isArray(subObject)) {
            Object.keys(subObject).forEach(subKey => {
              if (Array.isArray(subObject[subKey])) {
                if (!nestedArrayLengths[`${key}.${mapKey}`]) {
                  nestedArrayLengths[`${key}.${mapKey}`] = {};
                }
                nestedArrayLengths[`${key}.${mapKey}`][subKey] = Math.max(
                  nestedArrayLengths[`${key}.${mapKey}`][subKey] || 0,
                  subObject[subKey].length
                );
              }
            });
          }
        });
      }
    });
  });

  const expandedHeaders = [];
  Object.keys(formattedData[0]).forEach(key => {
    if (arrayLengths[key]) {
      for (let i = 0; i < arrayLengths[key]; i++) {
        const baseHeader = `${key}.${i}`;
        const item = formattedData[0][key][i];
        if (item && typeof item === 'object' && !Array.isArray(item)) {
          const nestedArrays = nestedArrayLengths[baseHeader] || {};
          Object.keys(item).forEach(subKey => {
            if (nestedArrays[subKey]) {
              const values = new Set();
              formattedData.forEach(row => {
                const subArray = row[key][i] ? row[key][i][subKey] || [] : [];
                subArray.forEach(val => values.add(val));
              });
              Array.from(values).sort().forEach(val => {
                expandedHeaders.push(`${baseHeader}.${subKey}.${val}`);
              });
            } else {
              expandedHeaders.push(`${baseHeader}.${subKey}`);
            }
          });
        } else {
          expandedHeaders.push(baseHeader);
        }
      }
    } else if (mapKeys[key]) {
      const keys = Array.from(mapKeys[key]).sort();
      keys.forEach(mapKey => {
        const subObject = formattedData[0][key][mapKey];
        if (subObject && typeof subObject === 'object' && !Array.isArray(subObject)) {
          const nestedArrays = nestedArrayLengths[`${key}.${mapKey}`] || {};
          Object.keys(subObject).forEach(subKey => {
            if (nestedArrays[subKey]) {
              const values = new Set();
              formattedData.forEach(row => {
                const subArray = row[key][mapKey] ? row[key][mapKey][subKey] || [] : [];
                subArray.forEach(val => values.add(val));
              });
              Array.from(values).sort().forEach(val => {
                expandedHeaders.push(`${key}.${mapKey}.${subKey}.${val}`);
              });
            } else {
              expandedHeaders.push(`${key}.${mapKey}.${subKey}`);
            }
          });
        }
      });
    } else {
      expandedHeaders.push(key);
    }
  });

  const expandedData = formattedData.map(row => {
    const newRow = {};
    Object.keys(row).forEach(key => {
      const value = row[key];
      if (arrayLengths[key]) {
        for (let i = 0; i < arrayLengths[key]; i++) {
          const baseHeader = `${key}.${i}`;
          const item = value[i];
          if (item && typeof item === 'object' && !Array.isArray(item)) {
            const nestedArrays = nestedArrayLengths[baseHeader] || {};
            Object.keys(item).forEach(subKey => {
              if (nestedArrays[subKey]) {
                const subArray = item[subKey] || [];
                const values = new Set();
                formattedData.forEach(r => {
                  const sa = r[key][i] ? r[key][i][subKey] || [] : [];
                  sa.forEach(val => values.add(val));
                });
                Array.from(values).sort().forEach(val => {
                  newRow[`${baseHeader}.${subKey}.${val}`] = subArray.includes(val) ? val : '';
                });
              } else {
                newRow[`${baseHeader}.${subKey}`] = item[subKey] !== undefined ? item[subKey] : '';
              }
            });
          } else {
            newRow[baseHeader] = value[i] !== undefined ? value[i] : '';
          }
        }
      } else if (mapKeys[key]) {
        const keys = Array.from(mapKeys[key]);
        keys.forEach(mapKey => {
          const subObject = value && value[mapKey] ? value[mapKey] : {};
          const nestedArrays = nestedArrayLengths[`${key}.${mapKey}`] || {};
          Object.keys(subObject).forEach(subKey => {
            if (nestedArrays[subKey]) {
              const subArray = subObject[subKey] || [];
              const values = new Set();
              formattedData.forEach(r => {
                const sa = r[key][mapKey] ? r[key][mapKey][subKey] || [] : [];
                sa.forEach(val => values.add(val));
              });
              Array.from(values).sort().forEach(val => {
                newRow[`${key}.${mapKey}.${subKey}.${val}`] = subArray.includes(val) ? val : '';
              });
            } else {
              newRow[`${key}.${mapKey}.${subKey}`] = subObject[subKey] !== undefined ? subObject[subKey] : '';
            }
          });
        });
      } else {
        newRow[key] = value;
      }
    });
    return newRow;
  });

  return { expandedHeaders, expandedData };
};

/*
 * Unflattens a flat row back into a nested structure for Firestore.
 * @param {Object} flattenedRow - The flat row to unflatten.
 * @returns {Object} - The nested object structure.
 */
const unflattenData = (flattenedRow) => {
  const result = {};
  let idValue = null;

  Object.keys(flattenedRow).forEach(key => {
    const value = flattenedRow[key];

    if (key === 'id') {
      idValue = value;
      return;
    }

    if (!key.includes('.')) {
      result[key] = value;
      return;
    }

    const parts = key.split('.');
    const rootKey = parts[0];

    if (parts.length === 2 && !isNaN(parts[1])) {
      const index = parseInt(parts[1], 10);
      result[rootKey] = result[rootKey] || [];
      result[rootKey][index] = value;
    } else {
      let current = result;
      for (let i = 0; i < parts.length; i++) {
        const part = parts[i];
        const isLastPart = i === parts.length - 1;

        if (i === 0) {
          current[part] = current[part] || {};
          current = current[part];
        } else {
          if (isLastPart) {
            current[part] = value;
          } else {
            current[part] = current[part] || {};
            current = current[part];
          }
        }
      }
    }
  });

  if (idValue !== null) {
    result.id = idValue;
  }

  return result;
};

/*
 * Calculates the maximum timestamp from 'created' and 'updated' fields in selected records.
 * @param {string} activeConnection - The active data source ('db', 'file', 'drive').
 * @param {Array} tableData - The unfiltered data from Firestore.
 * @param {Array} selectedRecords - Indices of selected records.
 * @returns {string} - The maximum timestamp as a formatted string, or 'N/A'.
 */
const getMaxTimestamp = (activeConnection, tableData, selectedRecords) => {
  if (activeConnection !== 'db' || !tableData || tableData.length === 0) {
    return 'N/A';
  }

  let maxTimestamp = null;

  const selectedData = tableData.filter((_, index) => selectedRecords.includes(index));

  if (!selectedData || selectedData.length === 0) {
    return 'N/A';
  }


  selectedData.forEach((row, idx) => {
    ['created', 'updated'].forEach(field => {
      const value = row[field];

      if (value !== undefined && value !== null) {
        let timestamp = null;
        try {
          const parts = value.match(/^(\d{1,2})\s(\w+)\s(\d{4})\sat\s(\d{2}:\d{2}:\d{2})\s(\w+)$/);
          if (parts) {
            const [, day, month, year, time] = parts;
            const monthNames = [
              'January', 'February', 'March', 'April', 'May', 'June',
              'July', 'August', 'September', 'October', 'November', 'December'
            ];
            const monthIndex = monthNames.indexOf(month);
            if (monthIndex === -1) {
              throw new Error(`Invalid month name: ${month}`);
            }
            const dateString = `${year}-${String(monthIndex + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T${time}`;
            timestamp = new Date(dateString);
          } else {
            throw new Error(`Date string does not match expected format: ${value}`);
          }
        } catch (err) {
          console.error(`getMaxTimestamp: Row ${idx}, Field ${field}, Error parsing date:`, err.message);
          timestamp = null;
        }

        if (timestamp && !isNaN(timestamp.getTime())) {
          if (!maxTimestamp || timestamp > maxTimestamp) {
            maxTimestamp = timestamp;
          }
        }
      } 
    });
  });

  return maxTimestamp
    ? maxTimestamp.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        timeZone: 'Europe/Paris',
        timeZoneName: 'short',
      }).replace(',', ' at')
    : 'N/A';
};

/*
 * Saves data to the filesystem as CSV or Excel, optionally with a metadata file.
 * @param {Array} dataToSave - The data to save.
 * @param {string} finalFileName - The name of the file to save.
 * @param {string} format - The format ('csv' or 'excel').
 * @param {Object} options - Additional options for saving.
 * @param {boolean} options.isAutomationMode - Whether to generate a metadata file.
 * @param {string} options.activeConnection - The active data source ('db', 'file', 'drive').
 * @param {Array} options.tableData - The unfiltered data from Firestore.
 * @param {Array} options.selectedRecords - Indices of selected records.
 * @param {Array} options.selectedColumns - The columns to include in the data file.
 * @param {Object} options.sourceTable - The source table metadata (e.g., { name: 'tableName' }).
 * @returns {void}
 */
const saveToFileSystem = (dataToSave, finalFileName, format, options = {}) => {
  const {
    isAutomationMode = false,
    activeConnection,
    tableData,
    selectedRecords,
    selectedColumns,
    sourceTable,
  } = options;

  try {
    // If no selectedColumns provided, use all available columns from the first row
    const headers = selectedColumns || (dataToSave && dataToSave.length > 0 ? Object.keys(dataToSave[0]) : []);
    
    if (!headers || headers.length === 0) {
      throw new Error('No columns available to save');
    }

    if (format === 'csv') {

      // Create the data CSV file
      const csvRows = [];
      csvRows.push(headers.join(','));
      
      dataToSave.forEach(row => {
        const values = headers.map(header => {
          const value = row[header] !== undefined && row[header] !== null ? row[header] : '';
          // Properly escape CSV values
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return `"${String(value).replace(/"/g, '""')}"`;
        });
        csvRows.push(values.join(','));
      });
      
      const csvString = csvRows.join('\n');      
      const dataBlob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
      saveAs(dataBlob, finalFileName);

      // If in automation mode, create the metadata CSV file
      if (isAutomationMode) {
        const metaFileName = finalFileName.replace('.csv', '_meta.csv');
        const sourceTableName = sourceTable?.name || 'N/A';
        const targetFileName = finalFileName;
        const maxTimestampValue = getMaxTimestamp(activeConnection, tableData, selectedRecords);
        const currentTimestamp = new Date().toLocaleString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          timeZone: 'Europe/Paris',
          timeZoneName: 'short',
        }).replace(',', ' at');
        const frequency = 'daily';

        const metaData = [
          ['Source Table', 'Target Filename', 'Max Updated/Created', 'File Creation Timestamp', 'Frequency'],
          [sourceTableName, targetFileName, maxTimestampValue, currentTimestamp, frequency],
        ];

        const metaCsvString = metaData.map(row => row.map(val => `"${String(val).replace(/"/g, '""')}"`).join(',')).join('\n');
        const metaBlob = new Blob([metaCsvString], { type: 'text/csv;charset=utf-8;' });
        saveAs(metaBlob, metaFileName);
      }
    } else if (format === 'excel') {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Sheet1');
      
      if (dataToSave.length > 0) {
        // Use the headers for column definitions
        worksheet.columns = headers.map(header => ({ 
          header, 
          key: header, 
          width: 15 
        }));
        
        // Add data rows
        dataToSave.forEach(row => {
          const rowData = {};
          headers.forEach(col => {
            rowData[col] = row[col] !== undefined && row[col] !== null ? row[col] : '';
          });
          worksheet.addRow(rowData);
        });
        
        // Auto-fit columns
        worksheet.columns.forEach(column => {
          let maxLength = 0;
          column.eachCell({ includeEmpty: true }, cell => {
            const columnLength = cell.value ? cell.value.toString().length : 10;
            if (columnLength > maxLength) {
              maxLength = columnLength;
            }
          });
          column.width = Math.min(Math.max(maxLength + 2, 10), 50);
        });
      }
      
      workbook.xlsx.writeBuffer().then(buffer => {
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(blob, finalFileName);
      }).catch(error => {
        console.error('Error creating Excel file:', error);
        throw new Error(`Failed to create Excel file: ${error.message}`);
      });
    }
  } catch (err) {
    console.error('Error saving file:', err);
    throw new Error(`Failed to save the file: ${err.message}`);
  }
};

export {
  formatTimestamp,
  formatTimestampsInData,
  expandArrayColumns,
  unflattenData,
  getMaxTimestamp,
  saveToFileSystem,
};